/**
 * Inventory Items Manager Component
 * Main component that manages inventory items with selection and bulk actions
 */

import React, { useState, useEffect } from 'react';
import InventoryGrid from './InventoryGrid';
import BulkActions from './BulkActions';
import { useInventoryItems } from '../hooks/useInventoryItems';

const InventoryItemsManager = ({ productTypes = [], onEditItem }) => {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  // Default to grid on mobile, list on desktop
  const [viewMode, setViewMode] = useState(() => {
    return window.innerWidth < 640 ? 'grid' : 'list';
  });

  const {
    items,
    loading,
    error,
    fetchItems,
    updateItemStatus,
    deleteItem
  } = useInventoryItems();

  // Sync selectAll checkbox state with actual selections
  useEffect(() => {
    if (items.length > 0) {
      const allSelected = items.every(item => selectedItems.has(item.id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedItems, items]);

  // Handle individual item selection
  const handleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item.id)));
    }
    setSelectAll(!selectAll);
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedItems(new Set());
    setSelectAll(false);
  };

  // Handle bulk delete
  const handleBulkDelete = async (deletedItemIds) => {
    // Remove deleted items from local state
    await fetchItems(); // Refresh the list
    handleClearSelection(); // Clear selections
  };

  // Handle edit item
  const handleEditItem = (item) => {
    if (onEditItem && typeof onEditItem === 'function') {
      onEditItem(item);
    } else {
      console.warn('onEditItem prop not provided or not a function');
    }
  };

  // Handle delete item
  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Are you sure you want to delete ${item.itemNumber}?`)) {
      return;
    }

    try {
      await deleteItem(item.id);
      await fetchItems(); // Refresh list
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item. Please try again.');
    }
  };

  // Handle status change
  const handleStatusChange = async (item, newStatus) => {
    try {
      await updateItemStatus(item.id, newStatus);
      await fetchItems(); // Refresh list to show updated status
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  // Update select all state when items change
  useEffect(() => {
    if (items.length === 0) {
      setSelectAll(false);
    } else {
      setSelectAll(selectedItems.size === items.length && items.length > 0);
    }
  }, [selectedItems, items]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading inventory items...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-red-800">Error loading inventory items: {error}</div>
        <button
          onClick={fetchItems}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      <BulkActions
        selectedItems={selectedItems}
        onBulkDelete={handleBulkDelete}
        onClearSelection={handleClearSelection}
      />

      {/* View Mode Toggle - Hidden on Mobile */}
      <div className="flex justify-between items-center">
        <div className="hidden sm:flex space-x-2">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1 rounded text-sm ${
              viewMode === 'list'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            List
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`px-3 py-1 rounded text-sm ${
              viewMode === 'grid'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Grid
          </button>
        </div>

        {/* Select All - Only show on desktop when in list mode */}
        {viewMode === 'list' && items.length > 0 && (
          <div className="hidden sm:flex items-center space-x-2">
            <label className="flex items-center text-sm text-gray-600">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
              />
              Select All ({items.length} items)
            </label>
          </div>
        )}

        {/* Mobile: Show total items count */}
        <div className="sm:hidden text-sm text-gray-600">
          {items.length} item{items.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Inventory Grid */}
      <InventoryGrid
        items={items}
        productTypes={productTypes}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        onStatusChange={handleStatusChange}
        onSelectItem={handleSelectItem}
        selectedItems={selectedItems}
        selectAll={selectAll}
        onSelectAll={handleSelectAll}
        viewMode={viewMode}
      />
    </div>
  );
};

export default InventoryItemsManager;